/**
 * A branded, whole-row destination toggle for the Buffer publish modals.
 *
 * Replaces the native OS checkbox rows: each destination reads as its
 * platform (brand glyph in its own colour via ServiceIcon), the account
 * handle, and a "where the slides land" pill. The entire tile toggles;
 * selection uses the app's green channel-selected language plus a
 * brand-coloured halo on the icon. All hover/focus/selected/motion states
 * live in app.css (.ql-dest-*) so there's no inline-vs-:hover conflict.
 *
 * Shared by BetaPostCard + PostCard (and safe to reuse anywhere a channel
 * is picked) so the two carousel modals never drift.
 */
import type { ChannelService } from "../api";
import { ServiceIcon, serviceLabel } from "./SocialIcons";

const CheckMark = (): React.JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export function DestinationToggle({
  service,
  name,
  meta,
  note,
  selected,
  onToggle,
  disabled = false,
  notify,
  onNotifyToggle,
}: {
  service: ChannelService;
  /** Provider handle / channel name, e.g. "quotologiez". */
  name: string;
  /** Slide-routing caption, e.g. "all 3 slides" / "slide 1 only". */
  meta: string;
  /** Optional secondary pill for static annotations. */
  note?: string;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
  /** Publishing-mode pill state: true = Buffer notification (phone push
   * at dueAt, finish manually), false/undefined = direct publish. */
  notify?: boolean;
  /** When provided, renders the clickable direct/notify pill on every
   * row (dimmed while the row is unselected — the mode only applies to
   * channels that are actually posted). Clicks don't toggle the row. */
  onNotifyToggle?: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="ql-dest-tile"
      data-selected={selected ? "true" : "false"}
      aria-pressed={selected}
      onClick={onToggle}
      disabled={disabled}
    >
      <ServiceIcon service={service} size={36} radius={11} glow={selected} />
      <span className="ql-dest-meta">
        <span className="ql-dest-top">
          <span className="ql-dest-name">{serviceLabel(service)}</span>
          <span className="ql-dest-pill">{meta}</span>
          {note ? <span className="ql-dest-pill ql-dest-pill-warn">{note}</span> : null}
          {onNotifyToggle ? (
            <span
              className={`ql-dest-pill ql-dest-pill-toggle${notify ? " ql-dest-pill-warn" : ""}`}
              style={selected ? undefined : { opacity: 0.45 }}
              role="switch"
              aria-checked={notify ? "true" : "false"}
              title={
                notify
                  ? "Notification: Buffer sends a phone push at the scheduled time; you finish the post in the app (e.g. to add music). Click for direct publish."
                  : "Direct publish: Buffer posts automatically at the scheduled time. Click for a phone notification instead."
              }
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onNotifyToggle();
              }}
            >
              {notify ? "🔔 notify" : "direct"}
            </span>
          ) : null}
        </span>
        <span className="ql-dest-handle">@{name}</span>
      </span>
      <span className="ql-dest-check" aria-hidden="true">
        <CheckMark />
      </span>
    </button>
  );
}

/** Placeholder row for a carousel-capable service the operator hasn't
 * connected in Buffer yet. */
export function DestinationEmpty({ service }: { service: ChannelService }): React.JSX.Element {
  return <div className="ql-dest-empty">No {serviceLabel(service)} channel connected in Buffer.</div>;
}
